import axios, { AxiosError, AxiosRequestConfig } from "axios";
import posthog from "@/lib/posthog";

// Extend AxiosRequestConfig to include retry metadata
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retryCount?: number;
}

// Every API call in the app funnels through this one interceptor, so it's
// the single best place to catch failures generically -- previously nothing
// here tracked anything, every failed request just silently rejected up to
// whatever caught it (usually a toast, never analytics).
//
// Deliberately only 5xx and network errors (no response at all) -- a 4xx is
// almost always an expected, already-handled outcome (bad input, 401 on a
// routine auth check, 404 used as an existence probe), and tracking those
// as "errors" would mostly be noise, not signal.
function trackApiError(error: AxiosError): void {
  // A request the app cancelled itself, or one made while offline, is not a backend fault.
  if (error.code === "ERR_CANCELED") return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  const status = error.response?.status;
  if (status && status < 500) return;
  posthog.captureException(error, {
    url: error.config?.url,
    method: error.config?.method,
    status: status ?? "network_error",
  });
}

const api = axios.create({
  // Set NEXT_PUBLIC_API_URL in .env.local to point at a local backend
  // (e.g. http://localhost:4321/api) without ever touching this file.
  baseURL: process.env.NEXT_PUBLIC_API_URL || "https://api.spidra.io/api",
  withCredentials: true, // ⬅️ this is required for HttpOnly cookies to be sent
  timeout: 30000, // 30-second timeout to prevent indefinite hangs
});

// Retry logic for transient errors
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ code?: string }>) => {
    const config = error.config as CustomAxiosRequestConfig | undefined;
    const retryCount = config?._retryCount ?? 0;

    // Up to 3 retries, only for errors worth retrying. (The old _retry flag stopped it after one.)
    if (!config || !isTransientError(error) || retryCount >= 3) {
      trackApiError(error);
      return Promise.reject(error);
    }

    config._retryCount = retryCount + 1;

    const delay = Math.min(1000 * Math.pow(2, config._retryCount - 1), 5000);
    await new Promise((resolve) => setTimeout(resolve, delay));

    return api(config);
  }
);

function isTransientError(error: AxiosError<{ code?: string }>): boolean {
  // Cancelled on purpose (a page change, a newer poll): never resend it.
  if (error.code === "ERR_CANCELED") return false;

  // No response at all. For a POST or PATCH the server may already have done the work (created the account,
  // started the scrape) and only the answer was lost, so resending it signs up twice ("email already exists")
  // or runs a job twice. Only reads and idempotent writes are resent.
  if (!error.response) {
    const method = (error.config?.method || "get").toLowerCase();
    return method !== "post" && method !== "patch";
  }

  const status = error.response.status;
  const code = error.response.data?.code;

  // Retry on specific status codes
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }

  // Retry on specific error codes
  if (code === "QUEUE_UNAVAILABLE") {
    return true;
  }

  return false;
}

export default api;
